import { yupResolver } from '@hookform/resolvers/yup';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { PAID_ORDERS_LIST_KEY, PAID_ORDERS_ONE_KEY } from 'modules/sales/paid-order/constants';
import { PaidOrderService } from 'modules/sales/paid-order/services';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect } from 'react';
import { orderCompletedSchema } from 'modules/sales/settings/order-status/schemas/order-complete.schema';
import { ActionTakenEnum } from '../constants/action-taken.enum';

const initValues: any = {
  completed: true,
  actionTaken: ActionTakenEnum.NO_ACTION
}

const useOrderComplete = (orderId: string, defaultValues = initValues, onClose?: () => void) => {
  const { t } = useTranslation('order');
  const queryClient = useQueryClient();
  const { control, handleSubmit, reset: resetForm } = useForm({
    resolver: yupResolver(orderCompletedSchema),
    defaultValues,
  });

  useEffect(() => {
    if (defaultValues) resetForm(defaultValues);
  }, [defaultValues, resetForm]);

  const { mutate, reset: resetMutation, error, isPending: isLoading, isSuccess, data } = useMutation(
    (payload) => PaidOrderService.complete(orderId, payload),
    {
      onSuccess: (data, values) => {
        toast.success(t('successCompleted'));
        queryClient.invalidateQueries({ queryKey: [orderId, PAID_ORDERS_ONE_KEY] });
        queryClient.invalidateQueries({ queryKey: [PAID_ORDERS_LIST_KEY] });
        onClose?.();
      },
    },
  );

  const reset = useCallback(
    () => {
      resetForm()
      resetMutation()
    },
    [resetForm, resetMutation],
  )

  return {
    control,
    error,
    isLoading,
    isSuccess,
    data,
    reset,
    onSubmit: handleSubmit((values) => {
      mutate(values);
    }),
  };
};

export default useOrderComplete;
